import { Box, Grid } from "@mui/material";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import Loader from "../../../../components/common/loader/Loader";
import SubmitModalWithFormik from "../../../../components/common/modalPopup/SubmitModalWithFormik";
import getAxiosError from "../../../../app/api/axiosError";
import agent from "../../../../app/api/agent";

// type to recieve parameter for this component to initialize
interface ExportRatecardModalProps {
  clientId: number;
  clientName: string;
  closeExportRatecardModal: CallableFunction;
  onCompleted: CallableFunction;
}

interface ExportRatecardUiRequest {
  startDate: Dayjs;
  endDate: Dayjs;
}

// export ratecard modal within a date range
const ExportRatecardModal = (props: ExportRatecardModalProps) => {
  const initialValues: ExportRatecardUiRequest = {
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
  };

  // validation rule while adding new ratecard into the database
  const validationSchema = Yup.object().shape({
    startDate: Yup.date().required(),
    endDate: Yup.date()
      .required("Please select the end date")
      .min(Yup.ref("startDate"), "End date must be after start date"),
  });

  const formatDate = (date: Date): string => {
    const year = date.getFullYear().toString().padStart(4, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  const mapUiRequestToServerRequest = (
    uiRequest: ExportRatecardUiRequest
  ): {
    clientId: number;
    ratecardIds: string;
    startDate: string;
    endDate: string;
  } => {
    return {
      clientId: props.clientId,
      ratecardIds: "",
      startDate: formatDate(uiRequest.startDate.toDate()),
      endDate: formatDate(uiRequest.endDate.toDate()),
    };
  };

  // on form submit make server call to export ratecard data between date range
  const handleSubmit = async (
    uiRequest: ExportRatecardUiRequest,

    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      const serverRequest = mapUiRequestToServerRequest(uiRequest);
      const response = await agent.RatecardAgent.exportRatecardData({
        clientId: serverRequest.clientId,
        ratecardIds: serverRequest.ratecardIds,
        startDate: serverRequest.startDate,
        endDate: serverRequest.endDate,
      });

      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(response);
      downloadLink.download = `${props.clientName}-RatecardData-${serverRequest.startDate}-to-${serverRequest.endDate}.xlsx`;
      document.body.appendChild(downloadLink);
      downloadLink.click();

      // Cleanup
      document.body.removeChild(downloadLink);
    } catch (error) {
      const appError = getAxiosError(error);

      if (appError.code === "400")
        toast.error("No data found for export: " + appError.errorMessage);
      else toast.error("Error downloading file:" + appError.errorMessage);

      // Handle error
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={{ ...initialValues }}
        validationSchema={validationSchema}
        onSubmit={(values: ExportRatecardUiRequest, { setSubmitting }) => {
          setSubmitting(true);
          handleSubmit(values, setSubmitting);
        }}
      >
        {({
          values,
          setFieldValue,
          isSubmitting,
          errors,
          isValid,
          setFieldTouched,
        }) => {
          return (
            <SubmitModalWithFormik
              title="Export ratecard data"
              closeSubmitModal={props.closeExportRatecardModal}
              childErrorMessage={""}
              allowSubmit={false}
              size="xs"
              isLoading={false}
              submitButtonName="Export"
              closeModalAfterSubmit={false}
              fullWidth={false}
            >
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Loader isLoading={isSubmitting} />
                <Form>
                  <Grid container>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          name="startDate"
                          label="Select start date"
                          value={values.startDate}
                          disableFuture
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: "small",
                              margin: "normal",
                            },
                          }}
                          views={["year", "month", "day"]}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(`startDate`, newValue);
                              setFieldTouched("startDate", true);
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={12} md={12} lg={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          name="endDate"
                          label="Select end date"
                          value={values.endDate}
                          slotProps={{
                            popper: { popperOptions: { placement: "top" } },
                            textField: {
                              fullWidth: true,
                              size: "small",
                              margin: "normal",
                              error: !isValid && errors.endDate !== undefined,
                              helperText: isValid
                                ? ""
                                : `${errors.endDate ?? ""}`,
                            },
                          }}
                          disableFuture
                          views={["year", "month", "day"]}
                          onChange={(newValue: any) => {
                            if (newValue) {
                              setFieldValue(`endDate`, newValue);
                              setFieldTouched("endDate", true);
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </Grid>
                  </Grid>
                </Form>
              </Box>
            </SubmitModalWithFormik>
          );
        }}
      </Formik>
    </>
  );
};

export default ExportRatecardModal;
